/* istanbul ignore file */
import { useQuery } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { useState } from "react";
import { createPreviewObjects } from "~/components/Backroom/Preview";
import { CurrentStepProps } from "../getSideMenuItems";
import { ContinueButton } from "../ContinueButton";
import { strapiClient } from "~/strapi/api";
import { showToast } from "~/utils/toast";
import { Inputs } from "../useStoreForm";
import Button from "~/components/Button";
import { Spinner } from "~/components/Spinner";
import { getWebsiteUrl } from "~/utils/getWebsiteUrl";

export const previewObjects = createPreviewObjects("stripeVerificationStep", []);

export const FIELDS: Array<keyof Inputs> = [];

type AccountStatus = "not_created" | "pending" | "verified" | "restricted_soon";
type StatusResponse = { data: { status: AccountStatus; storeId?: number } };

export const StripeVerificationStep = ({
  stepId,
  onContinue,
  isSubmitting,
  store,
}: CurrentStepProps) => {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);

  const returnedFromStripe = router.query.step === "stripeVerification";
  const comingFromStripeLink = router.query.returnedFromStripe === "true";

  const { data: accountStatus, isFetching: isStatusFetching } = useQuery({
    queryKey: ["stripeAccountStatus", comingFromStripeLink],
    queryFn: async () => {
      const url = comingFromStripeLink
        ? "/stores/stripe-account-status?returnedFromStripe=true"
        : "/stores/stripe-account-status";
      const res = await strapiClient.get<StatusResponse>(url);
      return res.data.data;
    },
    enabled: true,
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const isVerified = accountStatus?.status === "verified";
  const isPending = accountStatus?.status === "pending";

  const goToStripe = async () => {
    setIsLoading(true);
    try {
      const returnUrl =
        getWebsiteUrl() + window.location.pathname + "?step=stripeVerification";
      const params = new URLSearchParams({ returnUrl, refreshUrl: returnUrl });

      const { data } = await strapiClient.get<{ data: { url: string } }>(
        "/stores/" + store.id + "/stripe-onboarding-link?" + params.toString()
      );
      window.location.href = data.data.url;
    } catch {
      showToast("danger", "Noget gik galt", "Prøv igen senere.");
      setIsLoading(false);
    }
  };

  return (
    <>
      <h1 className="text-3xl mb-6">Verificer din identitet</h1>
      <p className="text-secondary-600 mb-8">
        For at modtage betalinger skal du verificere din identitet hos Stripe,
        vores betalingspartner. Det tager typisk 5-10 minutter.
      </p>

      {returnedFromStripe && isStatusFetching ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : returnedFromStripe && isVerified && !isPending ? (
        <>
          <div className="bg-green-50 border border-green-200 rounded-lg p-5 mb-8">
            <p className="text-green-800 font-medium">Tak!</p>
            <p className="text-green-700 text-sm mt-1">
              Din verificering er sendt til Stripe. Det kan tage op til 24 timer
              at blive godkendt.
            </p>
          </div>
          <ContinueButton
            beforeContinue={() => onContinue(stepId)}
            isSubmitting={isSubmitting}
          />
        </>
      ) : (
        <Button
          variant="primary"
          size="lg"
          className="w-full flex justify-center"
          onClick={goToStripe}
          disabled={isLoading}
        >
          {isLoading ? <Spinner /> : "Gå til Stripe-verificering"}
        </Button>
      )}
    </>
  );
};
